import React, { PureComponent } from 'react'
import PropTypes from 'prop-types'
import { injectIntl, FormattedMessage } from 'react-intl'
import { Flex, Box, Copy, themeColor, themeMediaQuery, Hide } from '@elparking/components'
import styled from 'styled-components'

import DateTimeSelect from 'parking-availability/js/ui/DateTimeSelect'
import Warning from 'parking-availability/js/ui/Warning'

import { CALENDAR_DATETIME_FORMAT } from 'commons/js/constants'
import { toDate, maybeParseTimestamp, makeHourTimeSpan, makeMinuteTimeSpan } from 'commons/js/util/dateConverter'

import SyncBookingDates from 'commons/js/components/SyncBookingDates'

import messages from './messages'

function getStartDateError (errors) {
    return errors.find(({ field }) => field === 'start')
}

function getEndDateError (errors) {
    return errors.find(({ field }) => field === 'end')
}

const DatesContainer = styled(Flex)`
    border: 1px solid ${themeColor('gray20')};
    border-radius: 2px;
    background-color: ${themeColor('white')};
`

const DatesLabel = styled(Flex)`
    display: none;

    ${themeMediaQuery('medium')} {
        display: flex;
    }
`

const SeparatorBox = styled(Box)`
    height: 100%;
    border-left: 1px solid ${themeColor('gray20')};
    margin-left: -1px;
`

class DatesRange extends PureComponent {
    static propTypes = {
        intl: PropTypes.object,
        format: PropTypes.string,
        startDate: PropTypes.number,
        endDate: PropTypes.number,
        errors: PropTypes.arrayOf(PropTypes.shape({
            field: PropTypes.string,
            message: PropTypes.string,
        })),
        onChange: PropTypes.func,
    }

    static defaultProps = {
        format: CALENDAR_DATETIME_FORMAT,
        onChange: () => {},
    }

    renderStartDateSelect (props, onChange) {
        return (
            <DateTimeSelect
              value={props.value && toDate(maybeParseTimestamp(props.value))}
              placeholder={this.props.intl.formatMessage(messages.startDatePlaceholder)}
              format={this.props.format}
              dayPickerProps={{
                  month: props.month,
                  fromMonth: props.fromMonth,
                  disabledDays: props.disabledDays,
                  locale: this.props.intl.locale,
              }}
              timePickerProps={{
                  disabledHours: props.disabledHours,
              }}
              onChange={onChange}
              showNextButton
              onNext={() => this.endDateSelect.focus()}
            />
        )
    }

    renderEndDateSelect (props, onChange) {
        return (
            <DateTimeSelect
              ref={(element) => { this.endDateSelect = element }}
              value={props.value && toDate(maybeParseTimestamp(props.value))}
              placeholder={this.props.intl.formatMessage(messages.endDatePlaceholder)}
              format={this.props.format}
              dayPickerProps={{
                    month: props.month,
                    fromMonth: props.fromMonth,
                    disabledDays: props.disabledDays,
                    locale: this.props.intl.locale,
                }}
              timePickerProps={{
                    disabledHours: props.disabledHours,
                }}
              onChange={onChange}
              onNext={() => this.endDateSelect.blur()}
              showNextButton
            />
        )
    }

    renderStartDateError () {
        const error = getStartDateError(this.props.errors)

        if (!error) {
            return null
        }

        return (
            <Warning>{error.message}</Warning>
        )
    }

    renderEndDateError () {
        const error = getEndDateError(this.props.errors)

        if (!error) {
            return null
        }

        return (
            <Warning>{error.message}</Warning>
        )
    }

    render () {
        const { startDate, endDate, onChange } = this.props

        return (
            <SyncBookingDates
              startDate={startDate}
              endDate={endDate}
              timeSpan={makeHourTimeSpan(1)}
              minTimeSpan={makeMinuteTimeSpan(10)}
              onChange={onChange}
            >
                {({ startDateProps, endDateProps, onChangeStart, onChangeEnd }) => (
                    <Flex w='100%' flexWrap='wrap'>
                        <DatesLabel w='100%' mb='0.2em'>
                            <Box pl='0.5em' flex='1'>
                                <Copy as='span' padding='0' color='main' transform='uppercase'>
                                    <FormattedMessage
                                      defaultMessage='Entrada'
                                    />
                                </Copy>
                            </Box>
                            <Box pl='0.5em' flex='1'>
                                <Copy as='span' padding='0' color='main' transform='uppercase'>
                                    <FormattedMessage
                                      defaultMessage='Salida'
                                    />
                                </Copy>
                            </Box>
                        </DatesLabel>
                        <DatesContainer w='100%' flexWrap='wrap' alignItems='center'>
                            <Flex flexWrap='wrap' flex='1'>
                                <Hide lg>
                                    <Box w='100%' pl='1em' pt='0.5em'>
                                        <Copy as='span' color='gray60' fontWeight='medium' lineHeight='0' size='xxSmall' transform='uppercase'>
                                            {this.props.intl.formatMessage(messages.startDateLabel)}
                                        </Copy>
                                    </Box>
                                </Hide>
                                <Box w='100%'>
                                    {this.renderStartDateSelect(startDateProps, onChangeStart)}
                                </Box>
                            </Flex>
                            <Box py='0.5em' style={{height: '100%'}}>
                                <SeparatorBox />
                            </Box>
                            <Flex flexWrap='wrap' flex='1'>
                                <Hide lg>
                                    <Box w='100%' pl='1em' pt='0.5em'>
                                        <Copy as='span' color='gray60' fontWeight='medium' lineHeight='0' size='xxSmall' transform='uppercase'>
                                            {this.props.intl.formatMessage(messages.endDateLabel)}
                                        </Copy>
                                    </Box>
                                </Hide>
                                <Box w='100%'>
                                    {this.renderEndDateSelect(endDateProps, onChangeEnd)}
                                </Box>
                            </Flex>
                        </DatesContainer>
                    </Flex>
                )}
            </SyncBookingDates>
        )
    }
}

export default injectIntl(DatesRange)
